/**
 * The shared material + shader library. OWNED BY `look` (coupled visual concern, N10).
 *
 * CONTRACT (ARCHITECTURE.md §4.4)
 * ------------------------------
 *   ctx.materials.get(key, opts?)   → THREE.Material — SHARED. Do not mutate.
 *   ctx.materials.instanceOf(key)   → THREE.Material — a private clone you may mutate.
 *   ctx.materials.uniforms(key)     → shared uniform bag, read-only.
 *
 * WHY SHARED INSTANCES MATTER
 * ---------------------------
 * One material per key means one shader program per material family and one sort bucket per
 * key. The perf budget is ≤ 320 draw calls and ≤ 45 programs (§9); if every building made
 * its own MeshStandardMaterial we would blow the program budget before the city even
 * finished streaming in. If you need per-object variation, use `get(key, {tint})` — variants
 * are cached and still shared — or `instanceOf(key)` if you genuinely need a one-off.
 *
 * PER-INSTANCE COLOUR: `InstancedMesh.instanceColor` works with every material here without
 * any flag on your side — three enables USE_INSTANCING_COLOR from the attribute. Use it.
 *
 * PROGRAM DISCIPLINE
 * ------------------
 * Every opaque surface material uses `map` and nothing else — no normal/roughness/metalness
 * maps. That collapses the whole world onto a handful of programs. The only extra families
 * are alpha-tested (fence, foliage), emissive-mapped (windows, signs), unlit sprites, and
 * points. If you add a key, put it in an existing family.
 */

import * as THREE from 'three';
import { TextureFactory } from './textures.js';
import { MAT_TUNING as T } from './tuning.js';
import {
  ROAD, PATCH, MARKINGS, KERB, SIDEWALK, BRICK, STONE, RENDER_STUCCO, TRIM, WINDOW, ROOF,
  TOWER, METAL, CONE, BARREL, TRUCK, TRAFFIC, CHAR, SNOW, SLUSH, WATER, SIGN, FLORA, WOOD, FX,
  mixHex,
} from './palette.js';

/** Material families. Everything in a family compiles to the same shader program. */
const FAM = {
  SURFACE: 'surface',     // MeshStandardMaterial + map                (road, masonry, props)
  CUTOUT: 'cutout',       // MeshStandardMaterial + map + alphaTest    (fence, foliage)
  EMISSIVE: 'emissive',   // MeshStandardMaterial + map + emissiveMap  (windows, lit signs)
  TRANSLUCENT: 'trans',   // MeshStandardMaterial + map, transparent   (glass, puddle, wet)
  UNLIT: 'unlit',         // MeshBasicMaterial + map                   (neon, sprites, decals)
  POINTS: 'points',       // PointsMaterial + map                      (rain, snow)
};

export class MaterialLibrary {
  static id = 'materials';

  constructor(ctx) {
    this.ctx = ctx;
    this.id = 'materials';

    /** key → THREE.Material (the canonical shared instance). */
    this._cache = new Map();
    /** key → factory(). Populated in init(). */
    this._defs = new Map();
    /** Everything ever handed out, for dispose(). */
    this._owned = new Set();
    /** Private clones handed out by instanceOf(). */
    this._clones = new Set();

    this.tex = null;
    this.textures = {};

    /**
     * Shared uniform bag. Read-only for everyone but `look`. Any shader that wants to react
     * to weather reads these. Driven from ctx.time.sim so shots stay bit-identical (§7.3).
     */
    this.shared = {
      uTime: { value: 0 },
      uWetness: { value: T.weatherMat.wetness.thaw },
      uSnow: { value: 0 },
      uNight: { value: 0 },
      uFogColor: { value: new THREE.Color(0x8DC3FC) },
      uSunDir: { value: new THREE.Vector3(0.45, 0.72, 0.52) },
      uSunColor: { value: new THREE.Color(0xFFF2D6) },
      /** NDC per screen pixel. Drives `_minScreenWidth`; kept current in `onResize`. */
      uThinNdc: { value: new THREE.Vector2(2 / 1920, 2 / 1080) },
      /** Camera velocity in VIEW space, m/s. Drives `_rainShear`; pushed by `look` each frame. */
      uRainVel: { value: new THREE.Vector3() },
    };

    /** Current look state, mirrored from `look`. */
    this.state = { weather: 'thaw', wetness: T.weatherMat.wetness.thaw, snow: 0, night: 0 };

    this._frozen = false;
    this.ready = false;
  }

  /* ══════════════════════════════════════════════════════════════════════════ init ══ */

  async init() {
    const ctx = this.ctx;
    const rng = ctx.rng.fork('look.materials');
    const maxAniso = ctx.renderer && ctx.renderer.capabilities
      ? ctx.renderer.capabilities.getMaxAnisotropy() : 8;

    this.tex = new TextureFactory(rng, maxAniso);

    const build = (async () => {
      const f = this.tex;
      const t = this.textures;

      // Surfaces the camera lives on — full resolution.
      t.asphalt = f.asphalt();
      t.patchFresh = f.patchFresh();
      t.patchCured = f.patchCured();
      t.sidewalk = f.concrete({ base: SIDEWALK.concrete, name: 'sidewalk' });
      t.kerb = f.concrete({ base: KERB.top, joints: false, name: 'kerb' });
      t.marking = f.markingStripe();
      t.slush = f.slush();
      t.snow = f.snow();
      t.wet = f.wetSheen();

      await Promise.resolve();

      // Masonry.
      t.brickRed = f.brick({ lit: BRICK.redLit, mid: BRICK.redMid, shade: BRICK.redShade, name: 'brick.red' });
      t.brickOchre = f.brick({ lit: BRICK.ochreLit, mid: BRICK.ochreMid, shade: BRICK.ochreShade, name: 'brick.ochre' });
      t.brickOrange = f.brick({ lit: BRICK.orangeLit, mid: BRICK.orangeMid, shade: BRICK.orangeShade, name: 'brick.orange' });
      t.stone = f.stone();
      t.render = f.renderCoat({ base: RENDER_STUCCO.pale });
      t.roof = f.roof();

      await Promise.resolve();

      // Windows + props.
      const win = f.windowGrid();
      t.window = win.map;
      t.windowLit = win.emissive;
      t.cone = f.coneStripes({ dirty: 0 });
      t.coneKnocked = f.coneStripes({ dirty: 0.55 });
      t.barrel = f.barrelStripes();
      t.metalDark = f.brushedMetal({ base: METAL.staircaseBlack, name: 'metal.dark' });
      t.metalLight = f.brushedMetal({ base: METAL.galvanised, name: 'metal.light' });
      t.fence = f.chainLink();
      t.foliage = f.foliage();
      t.signPanel = f.signPanel();
      t.paintTruck = f.paintedPanel({ base: TRUCK.yellow, name: 'paint.truck' });
      t.paintCar = f.paintedPanel({ base: 0xFFFFFF, name: 'paint.car' });
      t.cloth = f.cloth({ base: 0xFFFFFF, name: 'cloth' });

      await Promise.resolve();

      // Sprites.
      t.spriteSoft = f.softSprite();
      t.spriteHard = f.hardSprite();
      t.spriteRain = f.rainStreak();
      t.spriteSnow = f.snowFlake();
      t.spriteSplash = f.splashRing();
      t.spriteTrail = f.trailSprite();
      t.decal = f.decalBlob();
      t.white = f.flat(0xFFFFFF, 'flat.white');

      /**
       * ⚠ DOWNTOWN TOWERS ARE GENERATED LAST, AND THAT POSITION IS LOAD-BEARING.
       *
       * Every generator above draws from ONE shared stream (`this.rng`, the `look.materials`
       * fork). Inserting a new generator in the middle shifts the draw count for every
       * texture after it, so the cones, barrels, fences, signage, paint and sprites would all
       * re-roll — the towers would silently repaint half the city and every graded shot with
       * it. §6 forks per SUBSYSTEM; ordering within a fork is still ours to protect.
       *
       * Appending is the cheap fix: nothing earlier can move, because nothing earlier runs
       * later. If you add another texture here, add it BELOW this block, not above it.
       *
       * The spandrel reuses the brushed-metal generator and the podium the concrete one, so
       * the tower set costs exactly ONE new generator (the curtain wall) and 4 textures.
       */
      const curtain = f.curtainWall();
      t.towerGlass = curtain.map;
      t.towerGlassLit = curtain.emissive;
      t.towerSpandrel = f.brushedMetal({ base: TOWER.spandrel, name: 'tower.spandrel' });
      t.towerPodium = f.concrete({ base: TOWER.podium, joints: true, name: 'tower.podium' });

      this._registerDefs();
      // Warm the cache for every published key so the first frame never compiles a shader
      // mid-render, and so a missing key fails loudly at boot rather than silently at 90 km/h.
      for (const key of Array.from(this._defs.keys())) this.get(key);
      this.applyLook(this.state);
      this.ready = true;
    })();

    ctx.assets.register(build);
    await build;
  }

  /* ═════════════════════════════════════════════════════════════════ public API ══ */

  /**
   * Shared material for `key`. DO NOT MUTATE the result.
   * @param {string} key
   * @param {{tint?:number, opacity?:number, emissive?:number, emissiveIntensity?:number,
   *          side?:number, map?:THREE.Texture}} [opts] — produces a cached VARIANT, still shared.
   */
  get(key, opts) {
    const id = opts ? key + '|' + variantKey(opts) : key;
    let m = this._cache.get(id);
    if (m) return m;

    const def = this._defs.get(key);
    if (!def) {
      console.warn(`[materials] unknown key "${key}" — returning debug magenta. Ask \`look\` to add it.`);
      m = this._missing();
      this._cache.set(id, m);
      return m;
    }

    if (!opts) {
      m = def();
      if (!m.name || m.name === '') m.name = key;
      this._cache.set(id, m);
      this._owned.add(m);
      this._applyToOne(m);
      return m;
    }

    const base = this.get(key);
    m = base.clone();
    m.name = id;
    // Keep the look metadata so weather updates still reach the variant.
    m.userData.look = { ...base.userData.look };
    if (opts.tint != null) {
      m.userData.look.baseColor = opts.tint;
      m.color.set(opts.tint);
    }
    if (opts.map) m.map = opts.map;
    if (opts.opacity != null) { m.opacity = opts.opacity; if (opts.opacity < 1) m.transparent = true; }
    if (opts.emissive != null && m.emissive) m.emissive.set(opts.emissive);
    if (opts.emissiveIntensity != null && 'emissiveIntensity' in m) {
      m.emissiveIntensity = opts.emissiveIntensity;
      m.userData.look.nightEmissive = null;
    }
    if (opts.side != null) m.side = opts.side;
    this._cache.set(id, m);
    this._owned.add(m);
    this._applyToOne(m);
    return m;
  }

  /**
   * A PRIVATE clone you own and may mutate freely. It will NOT receive weather updates —
   * if you want the weather response, use get() with a tint instead.
   */
  instanceOf(key, opts) {
    const src = this.get(key, opts);
    const m = src.clone();
    m.name = key + ':instance';
    m.userData.look = { ...(src.userData.look || {}) };
    this._clones.add(m);
    return m;
  }

  /** Shared uniform bag. Read-only. `key` is accepted for interface symmetry. */
  uniforms(/* key */) { return this.shared; }

  /** Every registered key, sorted. Used by the harness and by the material contact sheet. */
  keys() { return Array.from(this._defs.keys()).sort(); }

  has(key) { return this._defs.has(key); }

  /* ═══════════════════════════════════════════════════════════ weather response ══ */

  /**
   * Called by `look` whenever the weather blend moves. Mutates the SHARED instances —
   * that is the whole point: it costs zero draw calls and zero new programs.
   */
  applyLook(s) {
    this.state.weather = s.weather || this.state.weather;
    this.state.wetness = s.wetness != null ? s.wetness : this.state.wetness;
    this.state.snow = s.snow != null ? s.snow : this.state.snow;
    this.state.night = s.night != null ? s.night : this.state.night;

    this.shared.uWetness.value = this.state.wetness;
    this.shared.uSnow.value = this.state.snow;
    this.shared.uNight.value = this.state.night;
    if (s.fogColor) this.shared.uFogColor.value.copy(s.fogColor);
    if (s.sunDir) this.shared.uSunDir.value.copy(s.sunDir);
    if (s.sunColor) this.shared.uSunColor.value.copy(s.sunColor);

    for (const m of this._owned) this._applyToOne(m);
  }

  _applyToOne(m) {
    const meta = m.userData.look;
    if (!meta) return;
    const wet = this.state.wetness, snow = this.state.snow, night = this.state.night;

    // Roughness: wet surfaces go glossy. This is the single biggest night-rain read.
    if (meta.roughDry != null) {
      let r = meta.roughDry;
      if (meta.roughWet != null) r += (meta.roughWet - r) * wet * (meta.wetResponse != null ? meta.wetResponse : 1);
      if (meta.roughSnow != null) r += (meta.roughSnow - r) * snow * (meta.snowResponse != null ? meta.snowResponse : 0);
      m.roughness = r;
    }

    // WINTER MAP SWAP — must happen BEFORE the albedo block below, because the albedo
    // normalisation divides by the mean of whichever map is actually bound.
    if (meta.snowMap) {
      const want = snow > 0.45 ? meta.snowMap : meta.baseMap;
      if (m.map !== want) { m.map = want; m.needsUpdate = true; }
    }

    // Albedo: wet DARKENS (a genuinely wet road is darker, not just shinier); snow lightens
    // horizontal surfaces; night grades everything slightly cooler and down.
    if (meta.baseColor != null && m.color) {
      _c.set(meta.baseColor);
      const wr = meta.wetResponse != null ? meta.wetResponse : 0;
      if (wr > 0) _c.multiplyScalar(1 - T.weatherMat.wetDarken * wet * wr);
      const sr = meta.snowResponse != null ? meta.snowResponse : 0;
      if (sr > 0) _c.lerp(_snowCol, T.weatherMat.snowLighten * snow * sr);
      if (!meta.ignoreTint) {
        const tint = lerpTintHex(this.state.weather, night);
        if (tint !== 0xFFFFFF) { _t.set(tint); _c.multiply(_t); }
      }
      // ALBEDO NORMALISATION. When a key declares `albedo: true`, its `baseColor` is the
      // FINAL albedo and the map is detail only, so we divide out the map's own mean. Without
      // this the shader computes `map * color`, and every key whose map was generated FROM
      // the same palette hex renders that hex squared — #A0A0A1 asphalt shading as #5F5F5F,
      // #2B2C2E truck chassis as #0B0B0C. `_c` is in the linear working space here, which is
      // the space the multiply actually happens in, so this is exact.
      if (meta.albedo && m.map && m.map.userData && m.map.userData.meanLinear) {
        const mm = m.map.userData.meanLinear;
        _c.r /= mm.r; _c.g /= mm.g; _c.b /= mm.b;
      }
      m.color.copy(_c);
    }

    // Emissives that only exist at night (lit windows, sign faces, lamp lenses).
    if (meta.nightEmissive != null) m.emissiveIntensity = meta.nightEmissive * night;

    if (meta.envDry != null && meta.envWet != null) {
      m.envMapIntensity = meta.envDry + (meta.envWet - meta.envDry) * wet;
    }
  }

  /* ══════════════════════════════════════════════════════════════ harness hooks ══ */

  applyHarnessState(/* patch */) {
    // Weather is owned by `look`; it calls applyLook() for us. Nothing to force here.
  }

  harnessStats() {
    return {
      keys: this._defs.size,
      instances: this._cache.size,
      clones: this._clones.size,
      textures: this.tex ? this.tex.count() : 0,
      textureMB: this.tex ? this.tex.megabytes() : 0,
      wetness: +this.state.wetness.toFixed(3),
      snow: +this.state.snow.toFixed(3),
      night: +this.state.night.toFixed(3),
    };
  }

  freezeClocks() { this._frozen = true; this.shared.uTime.value = this.ctx.time.sim; }

  async reset() {
    this.state.weather = 'thaw';
    this.state.wetness = T.weatherMat.wetness.thaw;
    this.state.snow = 0;
    this.state.night = 0;
    this.applyLook(this.state);
  }

  /** Presentation tick, driven by `look` (materials is not in UPDATE_ORDER). */
  tick(simTime) { this.shared.uTime.value = simTime; }

  fixedUpdate() {}
  update() {}
  onResize(width, height) {
    if (width > 0 && height > 0) this.shared.uThinNdc.value.set(2 / width, 2 / height);
  }

  dispose() {
    for (const m of this._owned) m.dispose();
    for (const m of this._clones) m.dispose();
    this._owned.clear(); this._clones.clear(); this._cache.clear();
    if (this.tex) this.tex.dispose();
  }

  /* ═══════════════════════════════════════════════════════════════ definitions ══ */

  _missing() {
    const m = new THREE.MeshStandardMaterial({ color: 0xFF00FF, roughness: 0.6 });
    m.name = 'MISSING';
    this._owned.add(m);
    return m;
  }

  /** MeshStandardMaterial + map. The workhorse family. */
  _surface(map, baseColor, o = {}) {
    const m = new THREE.MeshStandardMaterial({
      map,
      color: 0xFFFFFF,
      roughness: o.rough != null ? o.rough : 0.9,
      metalness: o.metal != null ? o.metal : 0.0,
      envMapIntensity: o.env != null ? o.env : T.mat.envBuilding,
      side: o.side || THREE.FrontSide,
      transparent: !!o.transparent,
      opacity: o.opacity != null ? o.opacity : 1,
      depthWrite: o.depthWrite != null ? o.depthWrite : true,
      alphaTest: o.alphaTest || 0,
      polygonOffset: !!o.polygonOffset,
      polygonOffsetFactor: o.polygonOffsetFactor || 0,
      polygonOffsetUnits: o.polygonOffsetUnits || 0,
      emissive: o.emissive != null ? o.emissive : 0x000000,
      emissiveIntensity: o.emissiveIntensity != null ? o.emissiveIntensity : 1,
      emissiveMap: o.emissiveMap || null,
      flatShading: !!o.flatShading,
      toneMapped: o.toneMapped !== false,
    });
    m.color.set(baseColor);
    m.userData.look = {
      family: o.family || (o.alphaTest ? FAM.CUTOUT : o.emissiveMap ? FAM.EMISSIVE : o.transparent ? FAM.TRANSLUCENT : FAM.SURFACE),
      baseColor,
      // `albedo: true` — "baseColor IS the finished albedo; the map is detail only". See
      // `_applyToOne`. Only legal when the map bakes in a colour of its own; a map authored
      // white (paint, cloth, flat) must NOT opt in or it normalises its own hue away.
      albedo: !!o.albedo,
      baseMap: map || null,
      snowMap: o.snowMap || null,
      roughDry: m.roughness,
      roughWet: o.roughWet != null ? o.roughWet : null,
      roughSnow: o.roughSnow != null ? o.roughSnow : null,
      wetResponse: o.wetResponse != null ? o.wetResponse : 0,
      snowResponse: o.snowResponse != null ? o.snowResponse : 0,
      nightEmissive: o.nightEmissive != null ? o.nightEmissive : null,
      envDry: o.env != null ? o.env : T.mat.envBuilding,
      envWet: o.envWet != null ? o.envWet : (o.env != null ? o.env : T.mat.envBuilding),
      ignoreTint: !!o.ignoreTint,
    };
    return m;
  }

  /**
   * ══════ MINIMUM SCREEN-SPACE WIDTH — W5, "balustrades break into 1-px dashed stipple" ══
   *
   * `world` found it at 400 % and correctly refused to fix it from geometry; the `combo-hud`
   * critic then measured it at 100 %: "on the white building the verticals are literal
   * dashes at roughly 50 % duty cycle: ~3 px black, ~3 px gap, repeating across 14
   * balusters." Confirmed here at 4x on `harness/out-look/combo-hud.png` — the balcony
   * balusters are a dotted line and two of the three railings in that crop have decayed to
   * loose specks.
   *
   * A baluster is ~3 cm of iron. At 35 m and a 60 deg vertical fov that is 0.8 screen
   * pixels, so whether any given one survives depends on where the pixel centres happen to
   * fall — which is the definition of stipple, and is why no amount of AA fixes it. MSAA
   * raises the sample count but a 0.8 px bar still misses most 4x sample grids, and FXAA
   * cannot reconstruct a line it never received. The only fix is to stop asking the
   * rasteriser to resolve sub-pixel geometry: give the bar a FLOOR on its screen width.
   *
   * HOW. Push each vertex outward in CLIP space along its own view-space normal, by a fixed
   * number of pixels. Clip space rather than object space because the expansion has to be
   * measured in pixels and because `world` instances these with rotation and scale — a
   * displacement along the object normal would inherit both. Three terms make it behave:
   *
   *   · `nl / (nl + 0.35)` fades the push out for faces pointing AT the camera. Those do not
   *     define the silhouette, so pushing them only shears the geometry; the faces that
   *     widen a bar are the grazing ones, and this is exactly their weight.
   *   · `smoothstep(near, far, viewZ)` holds the near field at zero. A railing 8 m away is
   *     already 4 px wide and must not fatten — this is a repair for sub-pixel geometry, not
   *     a style.
   *   · the push is per-vertex and symmetric, so a bar grows by `2 * px` and stays centred.
   *     It does NOT grow along its length, so a railing does not lengthen.
   *
   * PROGRAM BUDGET (§9, ≤ 45). The variant is selected by a `#define`, and three already
   * folds `material.defines` into its own program cache key, so no `customProgramCacheKey`
   * is needed and every material carrying this define shares ONE extra program between them.
   * Measured cost is one program for the whole thin-metal family.
   */
  _minScreenWidth(m, o = {}) {
    const px = o.px != null ? o.px : 0.6;
    const near = o.near != null ? o.near : 11;
    const far = o.far != null ? o.far : 22;

    m.defines = Object.assign({}, m.defines, { PPM_THIN_MIN_WIDTH: '' });
    const thinNdc = this.shared.uThinNdc;
    const thinK = { value: new THREE.Vector3(px, near, far) };
    m.userData.thin = { thinNdc, thinK };

    m.onBeforeCompile = (shader) => {
      shader.uniforms.uThinNdc = thinNdc;
      shader.uniforms.uThinK = thinK;
      shader.vertexShader = shader.vertexShader
        .replace('void main() {', `
uniform vec2 uThinNdc;   // NDC per screen pixel
uniform vec3 uThinK;     // (pixels of half-expansion, near hold m, full-effect m)
void main() {`)
        .replace('#include <project_vertex>', `#include <project_vertex>
#ifdef PPM_THIN_MIN_WIDTH
  {
    // transformedNormal is view-space and already carries the instance matrix
    // (three's <defaultnormal_vertex>), so this survives per-instance rotation.
    vec2 nxy = vec2( transformedNormal.x * projectionMatrix[0][0],
                     transformedNormal.y * projectionMatrix[1][1] );
    float nl = length( nxy );
    if ( nl > 1e-5 ) {
      float vz = - mvPosition.z;
      float k = smoothstep( uThinK.y, uThinK.z, vz ) * ( nl / ( nl + 0.35 ) );
      gl_Position.xy += ( nxy / nl ) * uThinNdc * uThinK.x * k * gl_Position.w;
    }
  }
#endif`);
    };
    m.needsUpdate = true;
    return m;
  }

  /** MeshBasicMaterial + map. Unlit — for anything that must not be shaded (neon, sprites). */
  _unlit(map, baseColor, o = {}) {
    const m = new THREE.MeshBasicMaterial({
      map,
      transparent: o.transparent !== false,
      opacity: o.opacity != null ? o.opacity : 1,
      depthWrite: o.depthWrite != null ? o.depthWrite : false,
      depthTest: o.depthTest !== false,
      blending: o.blending || THREE.NormalBlending,
      side: o.side || THREE.DoubleSide,
      alphaTest: o.alphaTest || 0,
      polygonOffset: !!o.polygonOffset,
      polygonOffsetFactor: o.polygonOffsetFactor || 0,
      polygonOffsetUnits: o.polygonOffsetUnits || 0,
      fog: o.fog !== false,
      toneMapped: o.toneMapped !== false,
    });
    m.color.set(baseColor);
    if (o.gain) m.color.multiplyScalar(o.gain);
    m.userData.look = { family: FAM.UNLIT, baseColor: null, ignoreTint: true };
    return m;
  }

  /**
   * ══ RAIN STREAKS SHEAR WITH THE CAMERA — W12, handed over by `fx` and correctly routed ══
   *
   * `night-rain` critic: the streaks are all at the same near-vertical angle, which is wrong
   * for a camera at 73 km/h. `fx` grepped it to `src/render/weather.js` before writing any
   * code and flagged it rather than fixing it — that is workbench C4 working, and they were
   * right: `Precipitation` is `look`'s under §3 and `src/fx/` emits no rain.
   *
   * The existing slant (`weather.rainSlantX/Z`) is a fixed WORLD-space lean, so it is
   * identical whatever the camera is doing. What a windscreen actually shows is the drop's
   * apparent velocity — fall minus camera motion — and under a translating camera that is not
   * one angle across the frame: it radiates from the focus of expansion, exactly like the
   * camera motion blur in post.js, and for the same reason.
   *
   * So the angle is computed PER PARTICLE in the vertex shader, from the same analytic
   * screen-space velocity the blur uses, and the fragment shader rotates `gl_PointCoord`
   * about its centre by it. A THREE.Points sprite cannot be rotated any other way — there is
   * no per-point rotation attribute — and rebuilding rain as quads would cost 4x the vertices
   * and a second draw call for an effect that is two draw calls today.
   *
   * `uRainVel` is the camera velocity in VIEW space, m/s, pushed by `look` each frame; when it
   * is zero the shear term vanishes and the streaks fall straight, which is the correct
   * stationary answer. Deterministic: velocity is sim state and the rotation is a pure
   * function of it and the particle's own view position (§7.3).
   */
  _rainShear(m) {
    m.defines = Object.assign({}, m.defines, { PPM_RAIN_SHEAR: '' });
    const vel = this.shared.uRainVel;
    // Screen-space fall rate, in the same units as the camera term. Not physical metres per
    // second: it is the weight that decides how vertical a stationary-camera streak is, and
    // the shear angle is atan2 of the two. 1.0 puts a 26 m/s camera at ~35 degrees at 12 m,
    // which is what a windscreen at that speed looks like.
    const fall = { value: 1.0 };
    m.userData.rainShear = { vel, fall };
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uRainVel = vel;
      shader.uniforms.uRainFall = fall;
      shader.vertexShader = shader.vertexShader
        .replace('void main() {', 'uniform vec3 uRainVel;\nuniform float uRainFall;\nvarying vec2 vRainDir;\nvoid main() {')
        .replace('#include <project_vertex>', `#include <project_vertex>
{
  // Apparent screen velocity of this drop: its own fall, minus the camera's motion, both
  // projected. mvPosition is view space; -z is forward, so vz>0 means the drop is in front.
  float vz = max(0.35, -mvPosition.z);
  vec2 ndc = gl_Position.xy / max(gl_Position.w, 1e-4);
  // Camera translation term, identical in form to post.js motionVector().
  vec2 camTerm = -(vec2(uRainVel.x, uRainVel.y) + ndc * uRainVel.z) / vz;
  // Fall term: straight down the screen, and it does not attenuate with distance the way
  // parallax does, so it is what keeps a distant drop near-vertical.
  vec2 fallTerm = vec2(0.0, -uRainFall);
  vec2 d = camTerm * 0.55 + fallTerm;
  float dl = length(d);
  vRainDir = dl > 1e-5 ? d / dl : vec2(0.0, -1.0);
}`);
      /*
       * The sprite is authored as a VERTICAL streak, so the rotation goes on the LOOKUP: read
       * the texture in the streak's own frame and it lands along the apparent direction.
       *
       * three reads `gl_PointCoord` inside `<map_particle_fragment>` and offers no hook, so
       * the chunk is REPLACED with a faithful reimplementation rather than patched around it.
       * Aliasing gl_PointCoord by regex was the first attempt and it is exactly the kind of
       * clever string surgery that produced two build outages in this wave — this version has
       * no hidden coupling to anything except the chunk's own three defines, which are
       * reproduced verbatim below (three r185, map_particle_fragment.glsl.js).
       */
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', 'varying vec2 vRainDir;\nvoid main() {')
        .replace('#include <map_particle_fragment>', `
#if defined( USE_MAP ) || defined( USE_ALPHAMAP )
  #if defined( USE_POINTS_UV )
    vec2 uv = vUv;
  #else
    vec2 ppmPc = gl_PointCoord - 0.5;
    vec2 ppmUp = -vRainDir;
    vec2 ppmRt = vec2( ppmUp.y, -ppmUp.x );
    vec2 ppmRot = vec2( dot( ppmPc, ppmRt ), dot( ppmPc, ppmUp ) ) + 0.5;
    vec2 uv = ( uvTransform * vec3( ppmRot.x, 1.0 - ppmRot.y, 1 ) ).xy;
  #endif
#endif
#ifdef USE_MAP
  diffuseColor *= texture2D( map, uv );
#endif
#ifdef USE_ALPHAMAP
  diffuseColor.a *= texture2D( alphaMap, uv ).g;
#endif
`);
    };
    m.needsUpdate = true;
    return m;
  }

  _points(map, baseColor, o = {}) {
    const m = new THREE.PointsMaterial({
      map,
      size: o.size != null ? o.size : 0.1,
      sizeAttenuation: o.sizeAttenuation !== false,
      transparent: true,
      opacity: o.opacity != null ? o.opacity : 1,
      depthWrite: false,
      blending: o.blending || THREE.NormalBlending,
      fog: o.fog !== false,
      alphaTest: 0.02,
      toneMapped: true,
    });
    m.color.set(baseColor);
    m.userData.look = { family: FAM.POINTS, baseColor: null, ignoreTint: true };
    return m;
  }

  _registerDefs() {
    const t = this.textures;
    const M = T.mat;
    const D = (key, fn) => this._defs.set(key, fn);

    /* ─────────────────────────────────────────────────────── ROAD SURFACES ─────── */

    // The bright ribbon. Highest-value large surface in frame; everything else sits below it.
    D('road.asphalt', () => this._surface(t.asphalt, ROAD.asphaltLit, {
      albedo: true,
      rough: M.roadRoughDry, roughWet: M.roadRoughWet, roughSnow: M.roadRoughSnow,
      wetResponse: 1, snowResponse: 0.62, env: M.envRoad, envWet: M.envWetRoad,
      snowMap: t.slush,
    }));
    // Ruelles are warmer and browner than the streets — a real Montreal tell.
    D('road.ruelle', () => this._surface(t.asphalt, ROAD.ruelleLit, {
      albedo: true,
      rough: M.roadRoughDry, roughWet: M.roadRoughWet, wetResponse: 1, snowResponse: 0.5,
      env: M.envRoad, envWet: 1.7, snowMap: t.slush,
    }));
    D('road.patch.fresh', () => this._surface(t.patchFresh, PATCH.freshWet, {
      albedo: true,
      rough: M.patchFreshRough, roughWet: M.patchFreshRoughWet, wetResponse: 0.5,
      env: M.envFreshPatch, envWet: M.envFreshPatch * 1.6,
    }));
    D('road.patch.cured', () => this._surface(t.patchCured, PATCH.cured, {
      albedo: true,
      rough: M.patchCuredRough, roughWet: M.roadRoughWet, wetResponse: 1, snowResponse: 0.4,
      env: M.envRoad, envWet: 1.8,
    }));
    // The inside of an open hole. Near-black so the hole reads as a HOLE at 90 km/h.
    D('road.hole', () => this._surface(t.patchFresh, PATCH.void, { albedo: true, rough: 0.95, env: 0.15, ignoreTint: true }));
    D('road.markings', () => this._surface(t.marking, MARKINGS.white, {
      albedo: true,
      rough: M.markingRough, roughWet: 0.20, wetResponse: 0.8, transparent: true,
      depthWrite: false, polygonOffset: true,
      polygonOffsetFactor: M.markingPolygonOffsetFactor, polygonOffsetUnits: M.markingPolygonOffsetUnits,
      env: M.envRoad,
    }));
    D('road.markings.yellow', () => this.get('road.markings', { tint: MARKINGS.yellow }));
    D('road.slush', () => this._surface(t.slush, SLUSH.tracked, {
      albedo: true,
      rough: M.slushRough, roughWet: 0.16, wetResponse: 0.7, env: 1.3, envWet: 2.0, ignoreTint: true,
    }));
    D('road.puddle', () => this._surface(t.wet, WATER.puddleDay, {
      albedo: true,
      rough: M.puddleRough, transparent: true, opacity: M.puddleOpacity,
      depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -3,
      env: M.envPuddle, envWet: M.envPuddle * 1.3, family: FAM.TRANSLUCENT,
    }));
    D('road.sidewalk', () => this._surface(t.sidewalk, SIDEWALK.concrete, {
      albedo: true,
      rough: M.sidewalkRough, roughWet: 0.30, wetResponse: 0.85, snowResponse: 0.85,
      roughSnow: M.snowRough, env: 0.7, envWet: 1.4, snowMap: t.snow,
    }));
    D('road.curb', () => this._surface(t.kerb, KERB.top, {
      albedo: true,
      rough: M.kerbRough, roughWet: 0.32, wetResponse: 0.7, snowResponse: 0.7, env: 0.7,
      snowMap: t.snow,
    }));

    /* ─────────────────────────────────────────────────────────── BUILDINGS ─────── */

    D('building.brick', () => this._surface(t.brickRed, BRICK.redAlbedo, { albedo: true, rough: M.brickRough, env: M.envBuilding, wetResponse: 0.25 }));
    D('building.brick.ochre', () => this._surface(t.brickOchre, BRICK.ochreAlbedo, { albedo: true, rough: M.brickRough, env: M.envBuilding, wetResponse: 0.25 }));
    D('building.brick.orange', () => this._surface(t.brickOrange, BRICK.orangeAlbedo, { albedo: true, rough: M.brickRough, env: M.envBuilding, wetResponse: 0.25 }));
    D('building.brick.painted', () => this._surface(t.brickRed, BRICK.paintedRedLit, { albedo: true, rough: 0.86, env: M.envBuilding, wetResponse: 0.25 }));
    D('building.stone', () => this._surface(t.stone, STONE.greystoneAlbedo, { albedo: true, rough: M.stoneRough, env: M.envBuilding, wetResponse: 0.3 }));
    D('building.render', () => this._surface(t.render, RENDER_STUCCO.pale, { albedo: true, rough: M.renderRough, env: M.envBuilding, wetResponse: 0.2 }));
    D('building.render.white', () => this._surface(t.render, RENDER_STUCCO.white, { albedo: true, rough: M.renderRough, env: M.envBuilding, wetResponse: 0.2 }));
    D('building.render.teal', () => this._surface(t.render, RENDER_STUCCO.teal, { albedo: true, rough: M.renderRough, env: M.envBuilding, wetResponse: 0.2 }));
    D('building.render.terracotta', () => this._surface(t.render, RENDER_STUCCO.terracotta, { albedo: true, rough: M.renderRough, env: M.envBuilding, wetResponse: 0.2 }));
    // PALE LIMESTONE LINTEL. `world` reported the old #6D6457 base could never carry a
    // lintel band; the dark pressed-metal cornice moved to its own key rather than being lost.
    D('building.trim', () => this._surface(t.render, TRIM.lintel, { albedo: true, rough: M.trimRough, env: 0.8, wetResponse: 0.3 }));
    D('building.cornice', () => this._surface(t.render, TRIM.cornice, { albedo: true, rough: M.trimRough, env: 0.8, wetResponse: 0.3 }));
    D('building.trim.white', () => this._surface(t.render, TRIM.frameWhite, { albedo: true, rough: M.trimRough, env: 0.8, wetResponse: 0.3 }));
    D('building.trim.green', () => this._surface(t.render, TRIM.frameGreen, { albedo: true, rough: M.trimRough, env: 0.8, wetResponse: 0.3 }));
    D('building.stone.sill', () => this._surface(t.render, STONE.sill, { albedo: true, rough: M.stoneRough, env: 0.7, wetResponse: 0.3 }));
    // The window grid carries its own night emissive so a facade lights up with zero extra geometry.
    D('building.window', () => this._surface(t.window, 0xFFFFFF, {
      rough: M.windowRough, metal: M.windowMetal, env: 1.2,
      emissiveMap: t.windowLit, emissive: 0xFFFFFF, emissiveIntensity: 0,
      nightEmissive: M.windowEmissiveNight, family: FAM.EMISSIVE, ignoreTint: true,
    }));
    // `instanceColor` multiplies DIFFUSE only — three never routes it into the emissive
    // slot — so it cannot dim a lit window. These variants set `emissiveIntensity`, which is
    // a uniform and not a define, so each one costs ZERO extra shader programs. `world` picks
    // a variant per building instead of trying to dim the shared key.
    const windowAt = (k) => this._surface(t.window, 0xFFFFFF, {
      rough: M.windowRough, metal: M.windowMetal, env: 1.2,
      emissiveMap: t.windowLit, emissive: 0xFFFFFF, emissiveIntensity: 0,
      nightEmissive: M.windowEmissiveNight * k, family: FAM.EMISSIVE, ignoreTint: true,
    });
    D('building.window.bright', () => windowAt(1.35));
    D('building.window.dim', () => windowAt(0.45));
    D('building.window.dark', () => windowAt(0.0));
    D('building.glass', () => this._surface(t.white, WINDOW.glassDay, {
      rough: M.glassRough, metal: M.glassMetal, transparent: true, opacity: M.glassOpacity,
      env: M.envGlass, depthWrite: false, family: FAM.TRANSLUCENT,
    }));
    D('building.roof', () => this._surface(t.roof, ROOF.albedo, {
      albedo: true, rough: M.roofRough, env: 0.4, snowResponse: 0.9, roughSnow: M.snowRough, wetResponse: 0.3,
      snowMap: t.snow,
    }));

    /* ────────────────────────────────────────────────── DOWNTOWN TOWERS ─────── */
    //
    // Requested by `world` for the curtain-wall quadrant. All three sit in EXISTING families
    // (emissive-mapped, and plain surface) so the set costs zero new shader programs — see
    // the PROGRAM DISCIPLINE note at the top of this file.
    //
    // ⚠ THESE THREE DO NOT SET `ignoreTint`, AND THAT IS THE POINT.
    // `building.window` opts out of the weather tint because a lit apartment pane should look
    // the same in any weather. A tower is the opposite: it is the largest thing on the
    // skyline, so if it does not grade with the city it becomes the one object that ignores
    // the weather. Letting the tint through is what makes winter's #EDF2F6 read as a cold
    // whiteout on the glass, and what keeps the towers inside nightrain's #BFC4CC grade.
    //
    // The AMBER at night does not come from the tint (nightrain's tint is cool blue-grey) —
    // it arrives as light. `LIGHT.sun.nightrain` #9C8F76 is a warm sodium skyglow key and
    // `LIGHT.hemiSky.nightrain` #33281B is a warm floor, so a facade that responds normally
    // to the rig picks the amber up on its lit side and keeps a warm bottom in shadow. The
    // material's job is only to not fight that.
    D('building.tower.glass', () => this._surface(t.towerGlass, 0xFFFFFF, {
      // Map carries the blue-green; baseColor stays white so the tint and the wet-darken
      // multiply cleanly on top. Same arrangement as `building.window`, hence no `albedo`.
      rough: M.towerGlassRough, roughWet: M.towerGlassRoughWet, metal: M.towerGlassMetal,
      // The whole point of a glass tower in the rain: env goes up hard when soaked.
      env: M.envTowerGlass, envWet: M.envTowerGlassWet,
      wetResponse: 0.8,
      // snowResponse deliberately 0. `snowResponse` lightens an albedo toward snow-white to
      // fake settled snow, which is only ever correct on a surface snow can LAND on. A
      // curtain wall is vertical and sheds it; winter cools this key through the tint
      // instead. (§4.9c's sibling trap — the right weather channel, not just any weather.)
      emissiveMap: t.towerGlassLit, emissive: 0xFFFFFF, emissiveIntensity: 0,
      nightEmissive: M.towerGlassEmissiveNight, family: FAM.EMISSIVE,
    }));
    /**
     * Per-tower liveliness. Same reasoning as `building.window.bright/dim/dark` above, and
     * the same trap: `get(key, {emissiveIntensity})` NULLS `nightEmissive`, which would pin a
     * tower permanently lit and make it glow at noon. Pick a variant instead of overriding.
     * `nightEmissive` is a uniform, not a define, so these cost zero extra programs.
     */
    const towerAt = (k) => this._surface(t.towerGlass, 0xFFFFFF, {
      rough: M.towerGlassRough, roughWet: M.towerGlassRoughWet, metal: M.towerGlassMetal,
      env: M.envTowerGlass, envWet: M.envTowerGlassWet, wetResponse: 0.8,
      emissiveMap: t.towerGlassLit, emissive: 0xFFFFFF, emissiveIntensity: 0,
      nightEmissive: M.towerGlassEmissiveNight * k, family: FAM.EMISSIVE,
    });
    D('building.tower.glass.bright', () => towerAt(1.4));   // the one tower working late
    D('building.tower.glass.dim', () => towerAt(0.5));
    D('building.tower.glass.dark', () => towerAt(0.0));     // shuttered / under construction
    // The opaque band between floors. Low-key on purpose: it is the horizontal rhythm that
    // makes a tower read as storeys, and it must not compete with the glazing for value.
    D('building.tower.spandrel', () => this._surface(t.towerSpandrel, TOWER.spandrel, {
      albedo: true,
      rough: M.towerSpandrelRough, metal: M.towerSpandrelMetal, env: M.envTowerSpandrel,
      wetResponse: 0.45,
    }));
    // Street level. Wetter response than the shaft above it because this is the band that
    // actually gets splashed, and the player only ever sees a tower's podium up close.
    D('building.tower.podium', () => this._surface(t.towerPodium, TOWER.podium, {
      albedo: true,
      rough: M.towerPodiumRough, roughWet: 0.42, env: M.envTowerPodium, envWet: 1.5,
      wetResponse: 0.55,
    }));
    // The exterior staircase. Non-negotiable Montreal identifier — give it a real material.
    // …and a minimum screen width, or the balusters stipple into dashes (see
    // `_minScreenWidth`; W5). This is the most Montreal geometry in the game and it was
    // dissolving at exactly the distance a player sees most of it from.
    D('staircase.metal', () => this._minScreenWidth(this._surface(t.metalDark, METAL.staircaseBlack, {
      albedo: true,
      rough: M.staircaseRough, metal: M.staircaseMetalness, env: M.envMetal, wetResponse: 0.4,
    }), M.thinMinWidth));
    D('staircase.metal.white', () => this._minScreenWidth(this._surface(t.metalLight, METAL.staircaseWhite, {
      albedo: true,
      rough: M.staircaseRough, metal: 0.3, env: M.envMetal, wetResponse: 0.4,
    }), M.thinMinWidth));

    /* ───────────────────────────────────────────────────────────── SIGNAGE ─────── */

    D('sign.panel', () => this._surface(t.signPanel, SIGN.panelWhite, { albedo: true, rough: 0.7, env: 0.7 }));
    // A backlit box sign: lit at night, plain plastic by day.
    D('sign.emissive', () => this._surface(t.signPanel, SIGN.panelWhite, {
      albedo: true,
      rough: 0.55, env: 0.6, emissive: SIGN.panelWhite, emissiveIntensity: 0.15,
      nightEmissive: M.signEmissive, family: FAM.EMISSIVE, emissiveMap: t.white, ignoreTint: true,
    }));
    // Neon tubing. Unlit and pushed above 1.0 so it is what the bloom pass finds.
    const neon = (c) => this._unlit(t.white, c, { gain: M.neonEmissive, transparent: false, depthWrite: true, side: THREE.FrontSide });
    D('sign.neon', () => neon(SIGN.neonRed));
    D('sign.neon.green', () => neon(SIGN.neonGreen));
    D('sign.neon.cyan', () => neon(SIGN.neonCyan));
    D('sign.neon.amber', () => neon(SIGN.neonAmber));
    D('sign.neon.pink', () => neon(SIGN.neonPink));
    D('sign.neon.blue', () => neon(SIGN.neonBlue));
    D('sign.neon.white', () => neon(SIGN.neonWhite));
    D('sign.stop', () => this._surface(t.signPanel, SIGN.stopRed, { albedo: true, rough: 0.5, env: 0.9 }));
    const lens = (c) => this._unlit(t.white, c, { gain: 2.2, transparent: false, depthWrite: true, side: THREE.FrontSide });
    D('sign.signal.red', () => lens(SIGN.signalRed));
    D('sign.signal.amber', () => lens(SIGN.signalAmber));
    D('sign.signal.green', () => lens(SIGN.signalGreen));

    /* ─────────────────────────────────────────────────────────────── PROPS ─────── */

    // The cone. Highest-contrast object in the game — the white bands are load-bearing.
    D('prop.cone', () => this._surface(t.cone, 0xFFFFFF, {
      rough: M.coneRough, roughWet: 0.22, wetResponse: 0.6, env: M.envProp, envWet: 1.4, ignoreTint: true,
    }));
    D('prop.cone.knocked', () => this._surface(t.coneKnocked, 0xFFFFFF, {
      rough: M.coneRough + 0.12, roughWet: 0.3, wetResponse: 0.6, env: M.envProp, ignoreTint: true,
    }));
    D('prop.barrel', () => this._surface(t.barrel, 0xFFFFFF, { rough: 0.62, env: M.envProp, wetResponse: 0.5, ignoreTint: true }));
    D('prop.fence', () => this._surface(t.fence, METAL.galvanised, {
      albedo: true,
      rough: 0.55, metal: 0.6, alphaTest: 0.42, side: THREE.DoubleSide, env: M.envMetal,
      family: FAM.CUTOUT, wetResponse: 0.4,
    }));
    D('prop.tree', () => this._surface(t.foliage, FLORA.leafMid, {
      albedo: true, rough: 0.92, alphaTest: 0.45, side: THREE.DoubleSide, env: 0.5,
      family: FAM.CUTOUT, snowResponse: 0.35,
    }));
    D('prop.tree.bark', () => this._surface(t.metalDark, FLORA.bark, { albedo: true, rough: 0.95, env: 0.4, wetResponse: 0.4 }));
    D('prop.bike', () => this._surface(t.metalDark, TRAFFIC.bixiRed, { albedo: true, rough: 0.45, metal: 0.5, env: M.envMetal, wetResponse: 0.4 }));
    D('prop.hydrant', () => this._surface(t.paintCar, SIGN.panelRed, { albedo: true, rough: 0.55, env: M.envProp, wetResponse: 0.5 }));
    D('prop.bin', () => this._surface(t.paintCar, 0x2F4A34, { albedo: true, rough: 0.72, env: M.envProp, wetResponse: 0.5 }));
    D('prop.metal', () => this._surface(t.metalLight, METAL.galvanised, { albedo: true, rough: M.metalRough, metal: M.metalMetalness, env: M.envMetal, wetResponse: 0.4 }));
    // Ground cover must be an OPAQUE map: the foliage card's alpha-0 margin premultiplies to
    // black on canvas readback, and an opaque ground plane renders those texels as scorched
    // blotches. The cloth grain is authored white with full alpha, so the albedo tint owns
    // the colour and the plane stays solid.
    D('prop.grass', () => this._surface(t.cloth, FLORA.grassLit, { albedo: true, rough: 0.95, env: 0.4, snowResponse: 0.8 }));
    D('ground.grass', () => this._surface(t.cloth, FLORA.grassLit, {
      albedo: true, rough: 0.95, env: 0.4, snowResponse: 0.85, roughSnow: M.snowRough, snowMap: t.snow,
    }));
    // Timber. Requested by `world` for hoardings, ruelle fences and balcony decking. Reuses
    // the cloth grain (authored white) so it costs no extra texture and no extra program.
    D('prop.wood', () => this._surface(t.cloth, WOOD.bareLit, { albedo: true, rough: 0.88, env: 0.5, wetResponse: 0.35 }));
    D('prop.wood.painted', () => this._surface(t.cloth, WOOD.paintedGrey, { albedo: true, rough: 0.84, env: 0.5, wetResponse: 0.35 }));
    D('prop.wood.ply', () => this._surface(t.cloth, WOOD.plywood, { albedo: true, rough: 0.9, env: 0.5, wetResponse: 0.35 }));
    D('prop.wood.pole', () => this._surface(t.metalDark, WOOD.creosote, { albedo: true, rough: 0.94, env: 0.4, wetResponse: 0.4 }));
    D('prop.snowbank', () => this._surface(t.snow, SNOW.lit, { albedo: true, rough: M.snowRough, env: 0.55, ignoreTint: true }));
    D('prop.snowbank.dirty', () => this._surface(t.snow, SNOW.dirtyGrey, { albedo: true, rough: M.snowRough, env: 0.5, ignoreTint: true }));

    /* ─────────────────────────────────────────────────────────────── TRUCK ─────── */

    D('truck.paint', () => this._surface(t.paintTruck, 0xFFFFFF, {
      rough: M.truckPaintRough, metal: M.truckPaintMetal, env: 1.3, envWet: 1.7,
      roughWet: 0.20, wetResponse: 0.4, ignoreTint: true,
    }));
    D('truck.paint.white', () => this._surface(t.paintCar, TRUCK.white, {
      albedo: true,
      rough: M.truckPaintRough, metal: M.truckPaintMetal, env: 1.3, roughWet: 0.20, wetResponse: 0.4,
    }));
    D('truck.paint.blue', () => this._surface(t.paintCar, TRUCK.blue, {
      albedo: true,
      rough: M.truckPaintRough, metal: M.truckPaintMetal, env: 1.3, roughWet: 0.20, wetResponse: 0.4,
    }));
    D('truck.glass', () => this._surface(t.white, TRUCK.glass, {
      rough: 0.08, transparent: true, opacity: M.truckGlassOpacity,
      env: M.envGlass, depthWrite: false, family: FAM.TRANSLUCENT,
    }));
    D('truck.metal', () => this._surface(t.metalLight, METAL.chrome, {
      albedo: true,
      rough: M.truckMetalRough, metal: M.truckMetalness, env: M.envMetal,
    }));
    D('truck.chassis', () => this._surface(t.metalDark, TRUCK.chassis, { albedo: true, rough: 0.62, metal: 0.4, env: 0.8 }));
    D('truck.hotbox', () => this._surface(t.metalDark, TRUCK.hotBox, { albedo: true, rough: 0.72, metal: 0.35, env: 0.8 }));
    D('truck.tyre', () => this._surface(t.metalDark, TRUCK.tyre, { albedo: true, rough: 0.95, roughWet: 0.5, wetResponse: 0.5, env: 0.3 }));
    // Headlights / beacons / brake lights. Above 1.0 so they bloom.
    D('truck.light', () => this._unlit(t.white, TRUCK.headlight, { gain: M.truckLightEmissive, transparent: false, depthWrite: true, side: THREE.FrontSide }));
    D('truck.light.brake', () => this._unlit(t.white, TRUCK.brake, { gain: M.truckLightEmissive, transparent: false, depthWrite: true, side: THREE.FrontSide }));
    D('truck.light.beacon', () => this._unlit(t.white, TRUCK.beaconAmber, { gain: M.truckLightEmissive * 1.2, transparent: false, depthWrite: true, side: THREE.FrontSide }));

    /* ───────────────────────────────────────────────────────────── TRAFFIC ─────── */

    D('traffic.paint', () => this._surface(t.paintCar, TRAFFIC.paints[0], {
      albedo: true,
      rough: M.trafficPaintRough, metal: M.trafficPaintMetal, env: 1.2, roughWet: 0.20, wetResponse: 0.4,
    }));
    for (let i = 0; i < TRAFFIC.paints.length; i++) {
      const hex = TRAFFIC.paints[i];
      D(`traffic.paint.${i}`, () => this.get('traffic.paint', { tint: hex }));
    }
    D('traffic.glass', () => this._surface(t.white, TRAFFIC.glass, {
      rough: 0.08, transparent: true, opacity: 0.5, env: M.envGlass, depthWrite: false, family: FAM.TRANSLUCENT,
    }));
    D('traffic.tyre', () => this._surface(t.metalDark, TRUCK.tyre, { albedo: true, rough: 0.95, env: 0.3 }));
    D('traffic.light', () => this._unlit(t.white, TRUCK.headlight, { gain: M.truckLightEmissive, transparent: false, depthWrite: true, side: THREE.FrontSide }));
    D('traffic.light.brake', () => this._unlit(t.white, TRUCK.brake, { gain: M.truckLightEmissive, transparent: false, depthWrite: true, side: THREE.FrontSide }));

    /* ──────────────────────────────────────────────────────────── CHARACTER ────── */

    D('char.skin', () => this._surface(t.cloth, CHAR.skin[0], { albedo: true, rough: M.charRough, env: 0.5 }));
    for (let i = 0; i < CHAR.skin.length; i++) {
      const hex = CHAR.skin[i];
      D(`char.skin.${i}`, () => this.get('char.skin', { tint: hex }));
    }
    D('char.cloth', () => this._surface(t.cloth, CHAR.cloth[0], { albedo: true, rough: M.charRough, env: 0.5 }));
    for (let i = 0; i < CHAR.cloth.length; i++) {
      const hex = CHAR.cloth[i];
      D(`char.cloth.${i}`, () => this.get('char.cloth', { tint: hex }));
    }
    D('char.hivis', () => this._surface(t.cloth, CHAR.hiVis, { albedo: true, rough: 0.7, env: 0.6, ignoreTint: true }));

    /* ────────────────────────────────────────────────────────────────── FX ─────── */

    D('fx.particle.soft', () => this._unlit(t.spriteSoft, 0xFFFFFF, { depthWrite: false }));
    D('fx.particle.hard', () => this._unlit(t.spriteHard, 0xFFFFFF, { depthWrite: false }));
    D('fx.particle.additive', () => this._unlit(t.spriteSoft, 0xFFFFFF, { depthWrite: false, blending: THREE.AdditiveBlending }));
    D('fx.decal', () => this._unlit(t.decal, 0xFFFFFF, {
      depthWrite: false, polygonOffset: true,
      polygonOffsetFactor: M.decalPolygonOffsetFactor, polygonOffsetUnits: M.decalPolygonOffsetUnits,
    }));
    D('fx.spray', () => this._unlit(t.spriteSoft, WATER.spray, { depthWrite: false, blending: THREE.AdditiveBlending }));
    D('fx.trail', () => this._unlit(t.spriteTrail, 0xFFFFFF, { depthWrite: false }));
    D('fx.sparks', () => this._unlit(t.spriteHard, FX.sparkCore, { depthWrite: false, blending: THREE.AdditiveBlending, gain: 2.2 }));
    D('fx.asphalt', () => this._unlit(t.spriteHard, FX.asphaltHot, { depthWrite: false }));
    D('fx.steam', () => this._unlit(t.spriteSoft, FX.steam, { depthWrite: false, opacity: 0.55 }));
    D('fx.tyremark', () => this._unlit(t.decal, FX.tyreMark, {
      depthWrite: false, polygonOffset: true,
      polygonOffsetFactor: M.decalPolygonOffsetFactor, polygonOffsetUnits: M.decalPolygonOffsetUnits,
      opacity: 0.7,
    }));
    D('fx.particle.soft.points', () => this._points(t.spriteSoft, 0xFFFFFF, { size: 0.22 }));
    D('fx.particle.hard.points', () => this._points(t.spriteHard, 0xFFFFFF, { size: 0.14 }));

    /* ───────────────────────────────────────────────────────────── WEATHER ─────── */

    // ADDITIVE, deliberately. Rain and snow are thousands of overlapping semi-transparent
    // sprites in a single draw call, and alpha blending is order-dependent — which means the
    // result depends on the rasteriser's primitive ordering. That is not stable under the
    // harness's software rasteriser, and it made `winter-slush` fail the bit-reproducibility
    // gate (§7.3) with identical CPU state and ~12k differing pixels. Additive blending is
    // commutative, so the result is order-independent and the shot reproduces exactly.
    // It is also the physically sensible choice: both rain and snow are bright scatterers.
    D('weather.snow', () => this._points(t.spriteSnow, SNOW.bright, {
      size: 0.26, opacity: 1.0, blending: THREE.AdditiveBlending,
    }));
    // Rain streaks SHEAR with the camera. See `_rainShear`.
    D('weather.rain', () => this._rainShear(this._points(t.spriteRain, WATER.rain, {
      size: 0.34, opacity: 0.62, blending: THREE.AdditiveBlending,
    })));
    D('weather.splash', () => this._unlit(t.spriteSplash, WATER.splash, { depthWrite: false, opacity: 0.6 }));
    D('water.wet', () => this._surface(t.wet, WATER.wetAsphaltDay, {
      albedo: true,
      rough: M.puddleRough + 0.05, transparent: true, opacity: M.wetOverlayOpacity,
      depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1.5, polygonOffsetUnits: -2.5,
      env: M.envWetRoad, envWet: M.envWetRoad * 1.4, family: FAM.TRANSLUCENT,
    }));

    /* ─────────────────────────────────────────────────────────────────── SKY ───── */
    // `sky.dome` is created by LookSystem (it owns the shader) and installed via
    // `_installSky` so nobody else ever constructs a sky material. This is the fallback
    // used only if look failed to init.
    if (!this._defs.has('sky.dome')) {
      D('sky.dome', () => this._unlit(t.white, 0x8DC3FC, {
        transparent: false, depthWrite: false, fog: false, side: THREE.BackSide,
      }));
    }
  }

  /** LookSystem installs the real sky material here so `get('sky.dome')` returns it. */
  _installSky(material) {
    this._defs.set('sky.dome', () => material);
    this._cache.set('sky.dome', material);
  }
}

/* ══════════════════════════════════════════════════════════════════════════ utils ══ */

const _c = new THREE.Color();
const _t = new THREE.Color();
const _snowCol = new THREE.Color(SNOW.lit);

function variantKey(o) {
  let s = '';
  if (o.tint != null) s += 't' + o.tint.toString(16);
  if (o.opacity != null) s += 'o' + o.opacity.toFixed(3);
  if (o.emissive != null) s += 'e' + o.emissive.toString(16);
  if (o.emissiveIntensity != null) s += 'i' + o.emissiveIntensity.toFixed(2);
  if (o.side != null) s += 's' + o.side;
  if (o.map) s += 'm' + (o.map.name || o.map.uuid);
  return s;
}

/** Blend the per-weather albedo tint toward the night value. */
function lerpTintHex(weather, night) {
  const base = T.weatherMat.albedoTint[weather] != null ? T.weatherMat.albedoTint[weather] : 0xFFFFFF;
  if (night <= 0) return base;
  return mixHex(base, T.weatherMat.albedoTint.nightrain, night);
}

export default MaterialLibrary;
